/**
 * Carte de sélection de garantie pour création de contrat
 * Design inspiré de pricing cards avec header coloré
 */

import { Check, Pencil } from 'lucide-react';
import { WARRANTY_TYPES, type WarrantyListItem } from '@/features/warranties/types/warranty.types';
import { calculateMonthlyPrice } from '@/features/warranties/services/pricingCalculator';
import { AssistanceBadge } from '@/features/assistance/components/AssistanceBadge';
import { ASSISTANCE_LABEL } from '@/features/assistance/assistanceWording';

interface WarrantySelectionCardProps {
  warranty: WarrantyListItem;
  isSelected: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  compact?: boolean;
  hidePrices?: boolean;
  hideMonthlyPrice?: boolean;
  /**
   * Afficher la mention d'assistance dans les caractéristiques.
   *
   * Une PROP et non un hook : la carte est rendue N fois dans une grille et doit
   * rester purement présentationnelle, comme `hidePrices` / `hideMonthlyPrice`.
   * C'est l'appelant qui connaît la société du contrat et décide de la visibilité.
   */
  showAssistance?: boolean;
}

export const WarrantySelectionCard = ({
  warranty,
  isSelected,
  onSelect,
  onEdit,
  compact = false,
  hidePrices = false,
  hideMonthlyPrice = false,
  showAssistance = false,
}: WarrantySelectionCardProps) => {
  const isExtension = warranty.warrantyType === WARRANTY_TYPES.EXTENSION;
  // Couleur de la garantie (custom ou défaut affinicar-primary)
  const bandColor = warranty.color || '#8074af';

  return (
    <div
      onClick={onSelect}
      className={`
        tw-bg-white tw-rounded-2xl tw-shadow-sm tw-overflow-hidden tw-transition-all tw-border tw-cursor-pointer tw-relative
        tw-grid tw-grid-rows-[auto_auto_auto_1fr_auto] tw-h-full tw-group
        ${compact ? 'tw-min-w-[220px] tw-flex-shrink-0' : ''}
        ${isSelected
          ? 'tw-border-2 tw-border-affinicar-primary tw-shadow-md tw-shadow-affinicar-primary/20'
          : 'tw-border-gray-200 hover:tw-shadow-md hover:tw-border-gray-300'
        }
      `}
    >
      {/* Bandeau coloré */}
      <div
        className="tw-h-1.5"
        style={{ backgroundColor: bandColor }}
      />

      {/* Badge check sélection */}
      {isSelected && (
        <div className="tw-absolute tw-top-3 tw-right-3 tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-bg-affinicar-primary">
          <Check className="tw-w-3.5 tw-h-3.5 tw-text-white" />
        </div>
      )}

      {/* Nom de la garantie */}
      <div className={`tw-relative ${compact ? 'tw-py-2 tw-px-3 tw-min-h-[48px]' : 'tw-py-3 tw-px-4 tw-min-h-[56px]'} tw-flex tw-items-center tw-justify-center`}>
        {onEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="tw-absolute tw-top-1.5 tw-right-1.5 tw-p-1 tw-rounded-md tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-colors"
            title="Modifier"
          >
            <Pencil className="tw-w-3.5 tw-h-3.5 tw-text-gray-500" />
          </button>
        )}
        <div className="tw-text-center">
          {onEdit && (
            <span className={`tw-inline-block tw-bg-gray-100 tw-text-gray-500 tw-rounded-full tw-font-medium tw-mb-1 ${compact ? 'tw-px-1.5 tw-py-0 tw-text-[8px]' : 'tw-px-2 tw-py-0.5 tw-text-[10px]'}`}>
              Personnalisée
            </span>
          )}
          <h3 className={`tw-font-bold tw-text-center tw-leading-tight tw-mb-0 tw-line-clamp-3 tw-uppercase tw-tracking-wider tw-text-gray-600 ${compact ? 'tw-text-xs' : 'tw-text-sm'}`}>
            {warranty.name}
          </h3>
        </div>
      </div>

      {/* Prix principal */}
      {!hidePrices && <div className={`tw-text-center tw-border-b tw-border-gray-100 ${compact ? 'tw-py-2 tw-px-3' : 'tw-py-4 tw-px-4'}`}>
        {warranty.isCashPayment && warranty.isMensualisable && !hideMonthlyPrice ? (
          <>
            <div className={`tw-font-bold tw-text-gray-900 ${compact ? 'tw-text-xl' : 'tw-text-3xl'}`}>
              {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
              <span className={`tw-text-gray-500 ${compact ? 'tw-text-sm' : 'tw-text-lg'}`}>{'€'}</span>
              <span className={`tw-text-gray-500 tw-ml-1 ${compact ? 'tw-text-[10px]' : 'tw-text-xs'}`}>/TTC</span>
            </div>
            {!isExtension && (
              <div className={`tw-text-gray-500 tw-mt-1 ${compact ? 'tw-text-[10px]' : 'tw-text-xs'}`}>
                {calculateMonthlyPrice(
                  warranty.sellingPrice / warranty.duration,
                  warranty.monthlyPlateformFees
                ).toFixed(2)} {'€'} {'×'} {warranty.duration} mois
              </div>
            )}
          </>
        ) : !warranty.isCashPayment && warranty.isMensualisable ? (
          /* Cas 2: Mensuel uniquement */
          <>
            <div className={`tw-font-bold tw-text-gray-900 ${compact ? 'tw-text-xl' : 'tw-text-3xl'}`}>
              {warranty.sellingPrice.toFixed(2).replace('.', ',')}
              <span className={`tw-text-gray-500 ${compact ? 'tw-text-sm' : 'tw-text-lg'}`}>{'€'}</span>
              <span className={`tw-text-gray-500 tw-ml-1 ${compact ? 'tw-text-[10px]' : 'tw-text-xs'}`}>/TTC</span>
            </div>
            {!isExtension && (
              <div className={`tw-text-gray-500 tw-mt-1 ${compact ? 'tw-text-[10px]' : 'tw-text-xs'}`}>
                {'×'} {warranty.duration} mois
              </div>
            )}
          </>
        ) : (
          /* Cas 3: Comptant uniquement */
          <div className={`tw-font-bold tw-text-gray-900 ${compact ? 'tw-text-xl' : 'tw-text-3xl'}`}>
            {(warranty.sellingPrice || 0).toFixed(2).replace('.', ',')}
            <span className={`tw-text-gray-500 ${compact ? 'tw-text-sm' : 'tw-text-lg'}`}>{'€'}</span>
            <span className={`tw-text-gray-500 tw-ml-1 ${compact ? 'tw-text-[10px]' : 'tw-text-xs'}`}>/TTC</span>
          </div>
        )}
      </div>}

      {/* Liste des features */}
      <div className={`tw-space-y-1 tw-bg-gray-50 tw-rounded-lg ${compact ? 'tw-p-2 tw-mx-3 tw-mb-2' : 'tw-p-3 tw-mx-4 tw-mb-4'}`}>
        {/* 1. Durée */}
        <div className="tw-flex tw-items-start tw-gap-2">
          <div className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${compact ? 'tw-w-3 tw-h-3 tw-mt-0' : 'tw-w-4 tw-h-4 tw-mt-0.5'} tw-bg-affinicar-primary/20`}>
            <span className={`tw-rounded-full tw-bg-affinicar-primary ${compact ? 'tw-w-1 tw-h-1' : 'tw-w-1.5 tw-h-1.5'}`}></span>
          </div>
          <div className="tw-flex-1">
            <span className={`tw-text-gray-500 ${compact ? 'tw-text-[11px]' : 'tw-text-xs'}`}>
              Durée : <span className="tw-text-gray-900 tw-font-semibold">{isExtension ? '-' : `${warranty.duration} mois`}</span>
            </span>
          </div>
        </div>

        {/* 2. Limite kilométrique */}
        <div className="tw-flex tw-items-start tw-gap-2">
          <div className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${compact ? 'tw-w-3 tw-h-3 tw-mt-0' : 'tw-w-4 tw-h-4 tw-mt-0.5'} tw-bg-affinicar-primary/20`}>
            <span className={`tw-rounded-full tw-bg-affinicar-primary ${compact ? 'tw-w-1 tw-h-1' : 'tw-w-1.5 tw-h-1.5'}`}></span>
          </div>
          <div className="tw-flex-1">
            <span className={`tw-text-gray-500 ${compact ? 'tw-text-[11px]' : 'tw-text-xs'}`}>
              Km max : <span className="tw-text-gray-900 tw-font-semibold">{isExtension ? '-' : `${(warranty.kmLimit || 0).toLocaleString('fr-FR')} km`}</span>
            </span>
          </div>
        </div>

        {/* 3. Plafond financier */}
        <div className="tw-flex tw-items-start tw-gap-2">
          <div className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${compact ? 'tw-w-3 tw-h-3 tw-mt-0' : 'tw-w-4 tw-h-4 tw-mt-0.5'} tw-bg-affinicar-primary/20`}>
            <span className={`tw-rounded-full tw-bg-affinicar-primary ${compact ? 'tw-w-1 tw-h-1' : 'tw-w-1.5 tw-h-1.5'}`}></span>
          </div>
          <div className="tw-flex-1">
            <span className={`tw-text-gray-500 ${compact ? 'tw-text-[11px]' : 'tw-text-xs'}`}>
              Plafond : <span className="tw-text-gray-900 tw-font-semibold">{(warranty.financialCap || 0).toLocaleString('fr-FR')} {'€'}</span>
            </span>
          </div>
        </div>

        {/* 4. Nombre de pièces garanties */}
        <div className="tw-flex tw-items-start tw-gap-2">
          <div className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${compact ? 'tw-w-3 tw-h-3 tw-mt-0' : 'tw-w-4 tw-h-4 tw-mt-0.5'} tw-bg-affinicar-primary/20`}>
            <span className={`tw-rounded-full tw-bg-affinicar-primary ${compact ? 'tw-w-1 tw-h-1' : 'tw-w-1.5 tw-h-1.5'}`}></span>
          </div>
          <div className="tw-flex-1">
            <span className={`tw-text-gray-500 ${compact ? 'tw-text-[11px]' : 'tw-text-xs'}`}>
              Pièces : <span className="tw-text-gray-900 tw-font-semibold">{warranty.partsCount || 0} pièce{(warranty.partsCount || 0) > 1 ? 's' : ''}</span>
            </span>
          </div>
        </div>

        {/*
          5. Assistance — volontairement DANS le conteneur de caractéristiques.
          La grille de la carte est calibrée sur exactement 5 enfants
          (`tw-grid-rows-[auto_auto_auto_1fr_auto]`, dont le 3e disparaît déjà
          quand `hidePrices`) : un 6e enfant direct décalerait toutes les lignes.
          Ici on ne touche aucune ligne de grille, et le rendu compact suit.
        */}
        {showAssistance && (
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${compact ? 'tw-w-3 tw-h-3 tw-mt-0' : 'tw-w-4 tw-h-4 tw-mt-0.5'} tw-bg-affinicar-primary/20`}>
              <span className={`tw-rounded-full tw-bg-affinicar-primary ${compact ? 'tw-w-1 tw-h-1' : 'tw-w-1.5 tw-h-1.5'}`}></span>
            </div>
            <div className="tw-flex-1">
              <span className={`tw-text-gray-500 ${compact ? 'tw-text-[11px]' : 'tw-text-xs'}`}>
                {ASSISTANCE_LABEL} : <AssistanceBadge hasAssistance={warranty.assistanceFormulaId != null} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bouton de sélection */}
      <div className={compact ? 'tw-px-3 tw-pb-2' : 'tw-px-4 tw-pb-4'}>
        <button
          type="button"
          onClick={onSelect}
          className={`
            tw-w-full tw-rounded-xl tw-font-medium tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-2
            ${compact ? 'tw-px-2 tw-py-1.5 tw-text-[10px]' : 'tw-px-4 tw-py-2.5 tw-text-sm'}
            ${isSelected
              ? 'tw-text-white tw-shadow-lg tw-bg-affinicar-primary'
              : 'tw-bg-gray-50 tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-affinicar-primary hover:tw-text-white hover:tw-border-affinicar-primary'
            }
          `}
        >
          {isSelected && <Check size={compact ? 12 : 16} />}
          {isSelected ? 'Sélectionnée' : 'Sélectionner'}
        </button>
      </div>
    </div>
  );
};
